import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Loader2Icon, Maximize2Icon } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import {
  isColumnPrompted,
  type MatrixCellUi,
  type MatrixColumnDef,
} from "@/domains/matrix";
import type { AdvanceDirection } from "../types";
import { badgeClassIn, type BadgeColorMap } from "../lib/value-badge";
import { parseListItems } from "../lib/list-items";

// However narrow the column, this many chips are always shown, truncated.
const MIN_CELL_CHIPS = 3;

const MAX_EDITOR_LINES = 10;

// How many chips fit before the rest collapse into a +n; widths are estimated.
function chipsThatFit(items: string[], columnWidth: number): number {
  // Cell padding either side, plus room for the expand button.
  let room = columnWidth - 24 - 24;
  if (items.length > MIN_CELL_CHIPS) room -= 30;

  let count = 0;
  for (const item of items) {
    const width = Math.min(item.length * 6.6 + 22, 180) + 4;
    if (count >= MIN_CELL_CHIPS && width > room) break;
    room -= width;
    count += 1;
    if (room <= 0) break;
  }
  return Math.max(count, Math.min(MIN_CELL_CHIPS, items.length));
}

type MatrixCellProps = {
  value: string;
  column: MatrixColumnDef;
  status?: MatrixCellUi["status"];
  badgeColors?: BadgeColorMap;
  isSelected: boolean;
  isEditing: boolean;
  onSelect: () => void;
  onStartEdit: () => void;
  onCommit: (value: string) => void;
  // Enter saves and moves down, Shift+Enter up, Tab along the row.
  onCommitAndAdvance: (value: string, direction: AdvanceDirection) => void;
  onCancelEdit: () => void;
  onExpand: () => void;
  // The add row draws the closing line, so the last row omits its own.
  isLastRow: boolean;
};

export function MatrixCell({
  value,
  column,
  status,
  badgeColors,
  isSelected,
  isEditing,
  onSelect,
  onStartEdit,
  onCommit,
  onCommitAndAdvance,
  onCancelEdit,
  onExpand,
  isLastRow,
}: MatrixCellProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const hasCommittedRef = useRef(false);
  const [draft, setDraft] = useState(value);
  const [wasEditing, setWasEditing] = useState(isEditing);
  const isEditable = !isColumnPrompted(column);

  // Reset the draft when editing starts, without an effect.
  if (isEditing !== wasEditing) {
    setWasEditing(isEditing);
    if (isEditing) setDraft(value);
  }

  // Enter and blur can both land for one edit; only the first one counts.
  function commitOnce(next: string, direction?: AdvanceDirection) {
    if (hasCommittedRef.current) return;
    hasCommittedRef.current = true;
    if (direction) onCommitAndAdvance(next, direction);
    else onCommit(next);
  }

  useEffect(() => {
    if (!isEditing) return;
    hasCommittedRef.current = false;
    // Not on the next frame: one that never comes leaves the editor open but deaf.
    inputRef.current?.select();
  }, [isEditing]);

  useLayoutEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    field.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(field).lineHeight) || 20;
    const maxHeight = lineHeight * MAX_EDITOR_LINES;
    field.style.height = `${Math.min(field.scrollHeight, maxHeight)}px`;
    field.style.overflowY = field.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [draft, isEditing]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Stopped from propagating: the grid listens for these on the document.
    if (event.key === "Enter") {
      // The browser inserts nothing for Ctrl/Cmd+Enter, so splice it in by hand.
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
        const field = event.currentTarget;
        const start = field.selectionStart;
        const end = field.selectionEnd;
        setDraft(`${draft.slice(0, start)}\n${draft.slice(end)}`);
        requestAnimationFrame(() =>
          field.setSelectionRange(start + 1, start + 1),
        );
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      commitOnce(draft, event.shiftKey ? "up" : "down");
    } else if (event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();
      commitOnce(draft, event.shiftKey ? "left" : "right");
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      hasCommittedRef.current = true;
      onCancelEdit();
    }
  }

  const isEditingHere = isEditing && isEditable;

  return (
    <td
      className={cn(
        "border-border/80 relative border-r px-3 py-2.5 align-middle",
        !isLastRow && "border-b",
        isSelected && "bg-primary/5 ring-primary ring-2 ring-inset",
      )}
      onClick={onSelect}
      onDoubleClick={() => (isEditable ? onStartEdit() : onExpand())}
    >
      {isEditingHere ? (
        <textarea
          ref={inputRef}
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitOnce(draft)}
          onKeyDown={handleKeyDown}
          aria-label={`Value of ${column.label}`}
          className="block w-full min-w-0 resize-none overflow-hidden border-0 bg-transparent p-0 text-sm leading-5 outline-none"
        />
      ) : (
        <>
          <div className="min-w-0">
            {isColumnPrompted(column) ? (
              <PromptCellContent
                value={value}
                status={status}
                column={column}
                badgeColors={badgeColors}
              />
            ) : (
              <CellValue
                value={value}
                column={column}
                badgeColors={badgeColors}
              />
            )}
          </div>
          <ExpandCellButton column={column} onExpand={onExpand} />
        </>
      )}
    </td>
  );
}

function CellValue({
  value,
  column,
  badgeColors,
}: {
  value: string;
  column: MatrixColumnDef;
  badgeColors?: BadgeColorMap;
}) {
  if (column.type === "badge" && value) {
    return (
      <CellChips
        value={value}
        variant="badge"
        width={column.width}
        badgeColors={badgeColors}
      />
    );
  }
  if (column.type === "list" && value) {
    return <CellChips value={value} variant="list" width={column.width} />;
  }
  return (
    // No `block` beside the clamp: it wins, and the line count does nothing.
    <span className="text-muted-foreground line-clamp-3 break-words">
      {value || "-"}
    </span>
  );
}

/**
 * Badge and list cells hold several entries in one string. Both show the first
 * few as chips on one clipped line and count the rest; the full set lives in
 * the expanded view.
 */
function CellChips({
  value,
  variant,
  width,
  badgeColors,
}: {
  value: string;
  variant: "badge" | "list";
  width: number;
  badgeColors?: BadgeColorMap;
}) {
  const items = parseListItems(value);
  const visible = items.slice(0, chipsThatFit(items, width));
  const hidden = items.length - visible.length;

  return (
    <span className="flex min-w-0 items-center gap-1 overflow-hidden">
      {visible.map((item, index) => (
        <Badge
          key={`${item}-${index}`}
          variant={variant === "list" ? "secondary" : "default"}
          className={cn(
            "min-w-0 shrink",
            variant === "list"
              ? // Translucent, so a chip stays legible on a selected cell too.
                "bg-foreground/8 text-foreground border-transparent font-normal"
              : badgeClassIn(badgeColors, item),
          )}
        >
          <span className="truncate">{item}</span>
        </Badge>
      ))}
      {hidden > 0 ? (
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
          +{hidden}
        </span>
      ) : null}
    </span>
  );
}

function TypedCellValue({
  value,
  column,
  badgeColors,
}: {
  value: string;
  column: MatrixColumnDef;
  badgeColors?: BadgeColorMap;
}) {
  if (column.type === "badge" && value) {
    return (
      <CellChips
        value={value}
        variant="badge"
        width={column.width}
        badgeColors={badgeColors}
      />
    );
  }

  if (column.type === "list" && value) {
    return <CellChips value={value} variant="list" width={column.width} />;
  }

  return (
    <span className="text-foreground line-clamp-3 break-words text-sm">
      {value || "-"}
    </span>
  );
}

function PromptCellContent({
  value,
  status,
  column,
  badgeColors,
}: {
  value: string;
  status: MatrixCellUi["status"];
  column: MatrixColumnDef;
  badgeColors?: BadgeColorMap;
}) {
  if (status === "pending") {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="min-w-0 flex-1">
          <TypedCellValue
            value={value}
            column={column}
            badgeColors={badgeColors}
          />
        </span>
        <Badge
          variant="outline"
          className="bg-background text-muted-foreground shrink-0 text-[10px] font-normal"
        >
          Queued
        </Badge>
      </div>
    );
  }

  if (status === "in progress") {
    return (
      <div className="text-muted-foreground flex items-center gap-1.5">
        <Loader2Icon className="size-3.5 animate-spin" />
        <span className="text-xs">Running...</span>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="min-w-0 flex-1">
          <TypedCellValue
            value={value}
            column={column}
            badgeColors={badgeColors}
          />
        </span>
        <Badge
          variant="destructive"
          className="shrink-0 text-[10px] font-normal"
        >
          Failed
        </Badge>
      </div>
    );
  }

  // An `idle` cell has never been asked to run, so it reads as its value: empty
  // until the first run, and the last result afterwards.
  return (
    <TypedCellValue value={value} column={column} badgeColors={badgeColors} />
  );
}

function ExpandCellButton({
  column,
  onExpand,
}: {
  column: MatrixColumnDef;
  onExpand: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="icon-sm"
      aria-label={`Expand ${column.label}`}
      title="Expand"
      className="text-muted-foreground hover:text-primary hover:bg-surface-hover border-border/70 cell-hovered:opacity-100 absolute top-1/2 right-2 size-6 -translate-y-1/2 opacity-0 focus-visible:opacity-100"
      onClick={(event) => {
        event.stopPropagation();
        onExpand();
      }}
    >
      <Maximize2Icon className="size-3" />
    </Button>
  );
}
